/**
 * Admin community moderation — /admin/community/moderation
 *
 * Admin-only flag queue. Lists open flags by default; per-flag,
 * surfaces target preview + reporter + reason + optional note. Resolve
 * actions: resolve / dismiss + optional hide/lock/delete on the target.
 * Mirrors C4 design §6.5-§6.6.
 *
 * Non-admins get a polite 403 redirect to /community.
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  fetchAdminFlagQueue,
  resolveFlag,
  type FlagListItem,
  type FlagReason,
} from "@/lib/community-api";

const PAGE_SIZE = 50;

type ResolutionAction = "hide" | "lock" | "delete" | "ack-only";

type QueueState =
  | { kind: "loading" }
  | {
      kind: "ready";
      flags: FlagListItem[];
      total: number;
      nextOffset: number | null;
    }
  | { kind: "admin_only" }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

function reasonLabel(r: FlagReason): string {
  switch (r) {
    case "phi":
      return "PHI exposure";
    case "spam":
      return "Spam";
    case "harassment":
      return "Harassment";
    case "off_topic":
      return "Off-topic";
    case "other":
      return "Other";
  }
}

function targetLinkFor(f: FlagListItem): string | null {
  if (f.target_type === "thread") return `/community/t/${f.target_id}`;
  // Replies + profiles + conversations: links would need extra context
  // (the reply's parent thread, the profile's handle, conversation
  // participant) — defer those deep-links to a follow-up; admin still
  // sees the preview here.
  return null;
}

export interface AdminCommunityModerationPageProps {
  fetchFn?: typeof fetchAdminFlagQueue;
  resolveFn?: typeof resolveFlag;
  onUnauthenticated?: (returnTo: string) => void;
}

export function AdminCommunityModerationPage({
  fetchFn = fetchAdminFlagQueue,
  resolveFn = resolveFlag,
  onUnauthenticated = redirectToLogin,
}: AdminCommunityModerationPageProps = {}): React.JSX.Element {
  const [state, setState] = useState<QueueState>({ kind: "loading" });
  const [statusFilter, setStatusFilter] = useState<
    "open" | "reviewing" | "resolved" | "dismissed"
  >("open");
  const [offset, setOffset] = useState(0);

  const reload = async (
    nextStatus: typeof statusFilter,
    nextOffset: number,
  ): Promise<void> => {
    try {
      setState({ kind: "loading" });
      const resp = await fetchFn({
        status: nextStatus,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      setState({
        kind: "ready",
        flags: resp.flags,
        total: resp.total,
        nextOffset: resp.next_offset,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthenticated("/admin/community/moderation");
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setState({ kind: "admin_only" });
        return;
      }
      const msg =
        err instanceof Error
          ? err.message
          : "Could not load the moderation queue.";
      setState({ kind: "error", message: msg });
    }
  };

  useEffect(() => {
    void reload(statusFilter, offset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, offset]);

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-5xl px-4 py-12 sm:px-6"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading flag queue…
        </p>
      </section>
    );
  }

  if (state.kind === "admin_only") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center">
        <h1 className="text-2xl font-bold text-foreground">Admin only</h1>
        <p className="mt-4 text-muted-foreground">
          The community moderation queue is restricted to platform admins.
        </p>
        <Link
          to="/community"
          className="mt-6 inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Back to community
        </Link>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <p className="text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <nav className="text-xs text-muted-foreground">
        <Link to="/admin" className="hover:text-foreground">
          Admin
        </Link>{" "}
        <span aria-hidden="true">›</span> Community moderation
      </nav>

      <header className="mt-3 mb-6">
        <h1 className="text-3xl font-bold text-foreground">
          Community moderation
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Flag queue from member reports. Resolve, dismiss, or apply an
          action to the target. Every action writes an audit row.
        </p>
      </header>

      <div className="mb-4 flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Status:</span>
        {(["open", "reviewing", "resolved", "dismissed"] as const).map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={statusFilter === s}
            onClick={() => {
              setOffset(0);
              setStatusFilter(s);
            }}
            className={`rounded-md border px-3 py-1 font-medium ${
              statusFilter === s
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {state.flags.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No {statusFilter} flags.
        </p>
      ) : (
        <ol className="space-y-3">
          {state.flags.map((f) => (
            <li key={f.id}>
              <FlagCard
                flag={f}
                onResolve={async (resolution, action, note) => {
                  await resolveFn(f.id, {
                    resolution,
                    ...(note ? { note } : {}),
                    ...(action ? { action } : {}),
                  });
                  await reload(statusFilter, offset);
                }}
              />
            </li>
          ))}
        </ol>
      )}

      {(offset > 0 || state.nextOffset !== null) && (
        <nav className="mt-6 flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => {
              setOffset(Math.max(0, offset - PAGE_SIZE));
            }}
            disabled={offset === 0}
            className="rounded-md border border-border px-3 py-1 hover:bg-muted disabled:opacity-50"
          >
            ← Previous
          </button>
          <p className="text-xs text-muted-foreground">
            {offset + 1}–{Math.min(offset + state.flags.length, state.total)} of {state.total}
          </p>
          <button
            type="button"
            onClick={() => {
              if (state.nextOffset !== null) setOffset(state.nextOffset);
            }}
            disabled={state.nextOffset === null}
            className="rounded-md border border-border px-3 py-1 hover:bg-muted disabled:opacity-50"
          >
            Next →
          </button>
        </nav>
      )}
    </section>
  );
}

function FlagCard({
  flag,
  onResolve,
}: {
  flag: FlagListItem;
  onResolve: (
    resolution: "resolved" | "dismissed",
    action: "hide" | "lock" | "delete" | null,
    note: string,
  ) => Promise<void>;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<ResolutionAction>("ack-only");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const link = targetLinkFor(flag);

  const submit = async (
    resolution: "resolved" | "dismissed",
  ): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      const resolvedAction = action === "ack-only" ? null : action;
      // Reply/profile actions are limited per the C4 design — pass null
      // if the target type can't accept the requested action.
      const allowed =
        flag.target_type === "thread"
          ? resolvedAction
          : flag.target_type === "reply" &&
              (resolvedAction === "hide" || resolvedAction === "delete")
            ? resolvedAction
            : null;
      await onResolve(resolution, allowed, note);
      setOpen(false);
      setNote("");
      setAction("ack-only");
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Could not resolve flag.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article className="rounded-lg border border-border bg-white p-4">
      <header className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2 text-xs">
          <span className="font-semibold uppercase tracking-wider text-muted-foreground">
            {flag.target_type}
          </span>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 font-medium">
            {reasonLabel(flag.reason)}
          </span>
        </div>
        <time
          className="text-xs text-muted-foreground"
          dateTime={flag.created_at}
        >
          {new Date(flag.created_at).toLocaleString()}
        </time>
      </header>

      <div className="mt-3 space-y-1 text-sm">
        <p className="text-xs text-muted-foreground">
          Reported by{" "}
          <span className="font-medium text-foreground">
            {flag.reporter.display_name}
          </span>
          {flag.reporter.handle && (
            <span className="ml-1">(@{flag.reporter.handle})</span>
          )}
        </p>
        {flag.target_preview && (
          <blockquote className="mt-2 border-l-2 border-border pl-3 text-foreground italic">
            {flag.target_preview}
            {flag.target_preview.length >= 200 && "…"}
          </blockquote>
        )}
        {flag.note && (
          <p className="mt-2 text-sm text-foreground">
            <span className="text-xs text-muted-foreground">Reporter note:</span>{" "}
            {flag.note}
          </p>
        )}
        {link && (
          <p className="mt-2 text-xs">
            <Link to={link} className="text-primary underline">
              Open target
            </Link>
          </p>
        )}
      </div>

      {flag.status === "open" && (
        <footer className="mt-4 border-t border-border pt-3">
          {!open ? (
            <button
              type="button"
              onClick={() => {
                setOpen(true);
              }}
              className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
            >
              Resolve
            </button>
          ) : (
            <div className="space-y-3">
              {flag.target_type === "thread" && (
                <div className="flex flex-wrap gap-2 text-xs">
                  {(["ack-only", "hide", "lock", "delete"] as const).map(
                    (a) => (
                      <button
                        key={a}
                        type="button"
                        aria-pressed={action === a}
                        onClick={() => {
                          setAction(a);
                        }}
                        className={`rounded-md border px-3 py-1 font-medium ${
                          action === a
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {a === "ack-only" ? "Acknowledge only" : a}
                      </button>
                    ),
                  )}
                </div>
              )}
              {flag.target_type === "reply" && (
                <div className="flex flex-wrap gap-2 text-xs">
                  {(["ack-only", "hide", "delete"] as const).map((a) => (
                    <button
                      key={a}
                      type="button"
                      aria-pressed={action === a}
                      onClick={() => {
                        setAction(a);
                      }}
                      className={`rounded-md border px-3 py-1 font-medium ${
                        action === a
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {a === "ack-only" ? "Acknowledge only" : a}
                    </button>
                  ))}
                </div>
              )}
              <textarea
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
                placeholder="Resolution note (private to admins)"
                rows={2}
                maxLength={2_000}
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-xs"
              />
              {error && (
                <p className="text-xs text-red-700" role="alert">
                  {error}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    void submit("resolved");
                  }}
                  disabled={submitting}
                  className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? "Saving…" : "Mark resolved"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void submit("dismissed");
                  }}
                  disabled={submitting}
                  className="rounded-md border border-border px-3 py-1 text-xs font-medium hover:bg-muted disabled:opacity-50"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setAction("ack-only");
                    setNote("");
                  }}
                  disabled={submitting}
                  className="ml-auto rounded-md px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </footer>
      )}
    </article>
  );
}
